import { Purchase } from '@ankorstore/api-internal/Purchase';
import { FilterPaymentStatusEnum } from '@ankorstore/api-internal/data-contracts';
import { captureConvenienceException } from '@client/monolith/core/plugins/sentry/helper';

/**
 * Why an account is frozen, when it's more than a genuinely failed/outstanding payment —
 * distinguishes an overdue delayed order (e.g. a late delivery) from a mix of both.
 * 'outstanding'-only resolves to undefined: the existing amount-bearing copy already
 * covers that case correctly, no distinguishing message needed.
 *
 * Shared by the header pill (use-credit-limit-pill.ts, which also feeds the cart-page
 * frozen banner) and the retailer settings page's frozen card (aria-frozen-card.vue) —
 * both need this exact distinction from the same underlying `filter[payment_status]`
 * existence checks. Mirrors account-brand-spa's identical
 * app/layers/core/services/frozen-reason-service.ts (a separate repo, so duplicated
 * rather than shared — same one-time existence-check pattern, not worth a cross-repo
 * dependency for).
 */
export type FrozenReason = 'delayed' | 'both';

// Whether the retailer has at least one payment matching the given status. Deliberately
// fails soft: a throw here must not take down the caller — this distinction is a copy
// nicety on top of an already-working feature, not the feature itself.
async function hasPaymentsWithStatus(status: FilterPaymentStatusEnum): Promise<boolean> {
  try {
    const res = await new Purchase().listPurchaseOperations({
      'filter[payment_status]': status,
      'page[limit]': 1,
    });
    return (res.data?.length ?? 0) > 0;
  } catch (error) {
    captureConvenienceException(error);
    return false;
  }
}

function deriveFrozenReason(
  hasOutstandingPayment: boolean,
  hasDelayedPayment: boolean
): FrozenReason | undefined {
  if (hasOutstandingPayment && hasDelayedPayment) {
    return 'both';
  }
  if (hasDelayedPayment) {
    return 'delayed';
  }
  return undefined;
}

/**
 * Resolves the frozen reason via two parallel existence checks.
 *
 * Uses `delayed`, not `frozen_related`, for the delayed check: this needs the
 * overdue-upcoming-debit subset only, and `delayed` already resolves to exactly that on
 * the backend (ankorstore OperationIndexRequest — shares its case with the deprecated
 * `past_due`, unaffected by frozen_related being a separate case added alongside it).
 */
export async function fetchFrozenReason(): Promise<FrozenReason | undefined> {
  const [hasOutstandingPayment, hasDelayedPayment] = await Promise.all([
    hasPaymentsWithStatus(FilterPaymentStatusEnum.Failed),
    hasPaymentsWithStatus(FilterPaymentStatusEnum.Delayed),
  ]);
  return deriveFrozenReason(hasOutstandingPayment, hasDelayedPayment);
}
